import { useEffect, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Plus, Trash2, Newspaper, Download, Share2, Wand2, Loader2, Image as ImageIcon,
  Tv, FileText, Sparkles, X, Clock, Upload, Camera, RefreshCw,
} from "lucide-react";

const CATS = ["Produce", "Grocery", "Meat & Deli", "Dairy", "Bakery", "Other"];
const THEMES = [
  { key: "weekly", label: "Weekly Sale", headline: "WEEKLY SAVINGS", accent: "#B3122B" },
  { key: "spring", label: "Spring Sale", headline: "SPRING SALE", accent: "#2E9E5B" },
  { key: "summer", label: "Summer Sale", headline: "SUMMER SALE", accent: "#F59E0B" },
  { key: "ramadan", label: "Ramadan Sale", headline: "RAMADAN SALE", accent: "#1F6F5C" },
  { key: "eid", label: "Eid Sale", headline: "EID MUBARAK SALE", accent: "#0E7A6B" },
  { key: "christmas", label: "Christmas Sale", headline: "CHRISTMAS SALE", accent: "#B3122B" },
  { key: "thanksgiving", label: "Thanksgiving Sale", headline: "THANKSGIVING SALE", accent: "#B5651D" },
  { key: "black_friday", label: "Black Friday", headline: "BLACK FRIDAY DEALS", accent: "#111111" },
  { key: "new_year", label: "New Year Sale", headline: "NEW YEAR SALE", accent: "#7A0E5B" },
  { key: "diwali", label: "Diwali Sale", headline: "DIWALI SALE", accent: "#C2410C" },
];
const blankItem = () => ({ name: "", price: "", unit: "", category: "Produce", image_media_id: null, _img: null });

function ItemRow({ item, idx, onChange, onRemove }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/weekly-ads/item-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(idx, { ...item, image_media_id: data.media_id, _img: `${API}/media/${data.media_id}/file?t=${Date.now()}` });
    } catch {
      toast.error("Image upload failed");
    }
    setUploading(false);
  };

  const setPhoto = async (source) => {
    if (!(item.name || "").trim()) return toast.error("Enter the item name first");
    setPhotoBusy(true);
    try {
      const { data } = await api.post("/weekly-ads/generate-item-photo", { name: item.name, source });
      onChange(idx, { ...item, image_media_id: data.media_id, _img: `${API}/media/${data.media_id}/file?t=${Date.now()}` });
      if (source === "real" && data.source === "ai") toast.info("No real photo found — used an AI photo instead");
      else toast.success(source === "real" ? "Real photo added" : "AI photo generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't get a photo");
    }
    setPhotoBusy(false);
  };

  const busyIcon = uploading || photoBusy;

  return (
    <div className="flex items-center gap-2 py-2 border-b border-border/60" data-testid={`wa-item-${idx}`}>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button"
            className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center overflow-hidden flex-shrink-0 border border-border relative group"
            data-testid={`wa-item-img-${idx}`}>
            {busyIcon ? <Loader2 className="w-4 h-4 animate-spin" /> :
              item._img ? <img src={item._img} alt="" className="w-full h-full object-cover" /> :
                <ImageIcon className="w-4 h-4 text-muted-foreground" />}
            {!busyIcon && (
              <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/45 text-white">
                <Camera className="w-4 h-4" />
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          <DropdownMenuItem onSelect={() => setPhoto("ai")} data-testid={`wa-item-ai-${idx}`}>
            <Wand2 className="w-4 h-4 mr-2" /> {item.image_media_id ? "Re-roll AI photo" : "Generate AI photo"}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setPhoto("real")} data-testid={`wa-item-real-${idx}`}>
            <RefreshCw className="w-4 h-4 mr-2" /> Use a real photo
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => fileRef.current?.click()} data-testid={`wa-item-upload-${idx}`}>
            <Upload className="w-4 h-4 mr-2" /> Upload from device
          </DropdownMenuItem>
          {item.image_media_id && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => onChange(idx, { ...item, image_media_id: null, _img: null })}
                className="text-destructive" data-testid={`wa-item-clearimg-${idx}`}>
                <X className="w-4 h-4 mr-2" /> Remove photo
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Input value={item.name} onChange={(e) => onChange(idx, { ...item, name: e.target.value })}
        placeholder="Item name" spellCheck className="flex-[2] min-w-0" data-testid={`wa-item-name-${idx}`} />
      <Input value={item.price} onChange={(e) => onChange(idx, { ...item, price: e.target.value })}
        placeholder="2.99" className="w-20 flex-shrink-0" data-testid={`wa-item-price-${idx}`} />
      <Input value={item.unit} onChange={(e) => onChange(idx, { ...item, unit: e.target.value })}
        placeholder="LB" className="w-20 flex-shrink-0" data-testid={`wa-item-unit-${idx}`} />
      <Select value={item.category} onValueChange={(v) => onChange(idx, { ...item, category: v })}>
        <SelectTrigger className="w-32 flex-shrink-0" data-testid={`wa-item-cat-${idx}`}><SelectValue /></SelectTrigger>
        <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
      <button type="button" onClick={() => onRemove(idx)} className="text-muted-foreground hover:text-destructive flex-shrink-0" data-testid={`wa-item-remove-${idx}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function Builder({ initial, business, onSaved, onClose }) {
  const [theme, setTheme] = useState(initial?.theme || "weekly");
  const [title, setTitle] = useState(initial?.title || "WEEKLY SAVINGS");
  const [validFrom, setValidFrom] = useState(initial?.valid_from || "");
  const [validTo, setValidTo] = useState(initial?.valid_to || "");
  const [endDate, setEndDate] = useState(initial?.end_at ? String(initial.end_at).slice(0, 10) : "");
  const [startDate, setStartDate] = useState(initial?.start_at ? String(initial.start_at).slice(0, 10) : "");
  const [tvs, setTvs] = useState([]);
  const [targetTvs, setTargetTvs] = useState(initial?.target_tv_ids || []);
  useEffect(() => { api.get("/tvs").then((r) => setTvs(r.data)).catch(() => {}); }, []);
  const toggleTv = (id) => setTargetTvs((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  const [accent, setAccent] = useState(initial?.accent_color || "#B3122B");
  const [fName, setFName] = useState(initial?.footer_name ?? business?.name ?? "");
  const [fPhone, setFPhone] = useState(initial?.footer_phone ?? business?.phone ?? "");
  const [fWa, setFWa] = useState(initial?.footer_whatsapp ?? business?.whatsapp ?? "");
  const [fAddr, setFAddr] = useState(initial?.footer_address ?? business?.address ?? "");
  const [fWeb, setFWeb] = useState(initial?.footer_website ?? business?.website ?? "");
  const [items, setItems] = useState(
    initial?.items?.length
      ? initial.items.map((it) => ({ ...it, _img: it.image_media_id ? `${API}/media/${it.image_media_id}/file` : null }))
      : [blankItem(), blankItem(), blankItem(), blankItem()]
  );
  const [saving, setSaving] = useState(false);
  const [fixing, setFixing] = useState(false);

  const setItem = (i, v) => setItems((arr) => arr.map((x, k) => (k === i ? v : x)));
  const removeItem = (i) => setItems((arr) => arr.filter((_, k) => k !== i));
  const addItem = () => setItems((arr) => [...arr, blankItem()]);

  const autoCorrect = async () => {
    const names = items.map((it) => it.name || "");
    if (!names.some((n) => n.trim())) return;
    setFixing(true);
    try {
      const { data } = await api.post("/weekly-ads/fix-spelling", { names });
      setItems((arr) => arr.map((x, k) => ({ ...x, name: data.names[k] ?? x.name })));
      toast.success("Names cleaned up");
    } catch {
      toast.error("Auto-correct failed");
    }
    setFixing(false);
  };

  const pickTheme = (k) => {
    setTheme(k);
    const t = THEMES.find((x) => x.key === k);
    if (t) { setTitle(t.headline); setAccent(t.accent); }
  };

  const save = async () => {
    const clean = items.filter((it) => (it.name || "").trim());
    if (!clean.length) return toast.error("Add at least one item");
    setSaving(true);
    const body = {
      title, theme, valid_from: validFrom, valid_to: validTo, accent_color: accent,
      start_at: startDate ? new Date(startDate + "T00:00:00").toISOString() : null,
      end_at: endDate ? new Date(endDate + "T23:59:59").toISOString() : null,
      target_tv_ids: targetTvs,
      footer_name: fName, footer_phone: fPhone, footer_whatsapp: fWa, footer_address: fAddr, footer_website: fWeb,
      items: clean.map(({ name, price, unit, category, image_media_id }) => ({ name, price, unit, category, image_media_id })),
    };
    try {
      const res = initial ? await api.put(`/weekly-ads/${initial.id}`, body) : await api.post("/weekly-ads", body);
      toast.success("Weekly ad saved");
      onSaved(res.data);
    } catch (e) {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Occasion</Label>
          <Select value={theme} onValueChange={pickTheme}>
            <SelectTrigger className="mt-1" data-testid="wa-theme"><SelectValue /></SelectTrigger>
            <SelectContent>{THEMES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-2"><Label>Headline</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="wa-title" /></div>
        <div><Label>Valid from</Label>
          <Input value={validFrom} onChange={(e) => setValidFrom(e.target.value)} placeholder="SEPT 23" data-testid="wa-from" /></div>
        <div><Label>Valid to</Label>
          <Input value={validTo} onChange={(e) => setValidTo(e.target.value)} placeholder="SEPT 29, 2026" data-testid="wa-to" /></div>
        <div><Label>Auto-remove from TVs after</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="wa-enddate" />
          <p className="text-[11px] text-muted-foreground mt-1">Sale turns off on TVs automatically at end of this day.</p></div>
        <div><Label>Auto-publish on (start date)</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="wa-startdate" />
          <p className="text-[11px] text-muted-foreground mt-1">Sale turns on TVs automatically at the start of this day. Leave blank to publish manually.</p></div>
        <div className="flex items-end gap-2"><div><Label>Flyer color</Label>
          <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="h-10 w-16 rounded border border-border bg-transparent" data-testid="wa-accent" /></div></div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-base font-semibold">Items</Label>
          <Button type="button" size="sm" variant="secondary" onClick={autoCorrect} disabled={fixing} data-testid="wa-autocorrect">
            {fixing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />} Auto-correct names
          </Button>
        </div>
        <div className="rounded-lg border border-border p-2 max-h-[38vh] overflow-y-auto">
          {items.map((it, i) => (
            <ItemRow key={i} item={it} idx={i} onChange={setItem} onRemove={removeItem} />
          ))}
          <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={addItem} data-testid="wa-add-item">
            <Plus className="w-4 h-4 mr-1" /> Add item
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">TV slides show 4 items each; the PDF groups items by category.</p>
      </div>

      <details className="rounded-lg border border-border p-3">
        <summary className="text-sm font-medium cursor-pointer">Auto-publish to TVs (optional)</summary>
        <p className="text-xs text-muted-foreground mt-2">Pick the TVs this sale should switch on automatically on its start date (and switch off on its end date). Leave empty to just build the playlist without auto-assigning.</p>
        <div className="grid grid-cols-2 gap-2 mt-3">
          {tvs.length === 0 && <p className="text-xs text-muted-foreground col-span-2">No TVs yet.</p>}
          {tvs.map((tv) => (
            <label key={tv.id} className="flex items-center gap-2 text-sm cursor-pointer rounded-md border border-border px-3 py-2" data-testid={`wa-tv-${tv.id}`}>
              <input type="checkbox" checked={targetTvs.includes(tv.id)} onChange={() => toggleTv(tv.id)} />
              <span className="truncate">{tv.name}{tv.zone ? ` · ${tv.zone}` : ""}</span>
            </label>
          ))}
        </div>
      </details>

      <details className="rounded-lg border border-border p-3">
        <summary className="text-sm font-medium cursor-pointer">Footer / contact on PDF</summary>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="col-span-2"><Label>Store name</Label><Input value={fName} onChange={(e) => setFName(e.target.value)} data-testid="wa-fname" /></div>
          <div><Label>WhatsApp</Label><Input value={fWa} onChange={(e) => setFWa(e.target.value)} data-testid="wa-fwa" /></div>
          <div><Label>Phone</Label><Input value={fPhone} onChange={(e) => setFPhone(e.target.value)} data-testid="wa-fphone" /></div>
          <div className="col-span-2"><Label>Address</Label><Input value={fAddr} onChange={(e) => setFAddr(e.target.value)} data-testid="wa-faddr" /></div>
          <div className="col-span-2"><Label>Website</Label><Input value={fWeb} onChange={(e) => setFWeb(e.target.value)} data-testid="wa-fweb" /></div>
        </div>
      </details>

      <DialogFooter className="gap-2">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving} data-testid="wa-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function WeeklyAds() {
  const { currentBusiness } = useAuth();
  const [ads, setAds] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/weekly-ads").then((r) => setAds(r.data));
  useEffect(() => { load(); }, []);

  const generate = async (ad) => {
    setBusy(ad.id);
    try {
      await api.post(`/weekly-ads/${ad.id}/generate`);
      toast.info("Generating AI product photos, TV slides & PDF — this can take a minute…");
      let tries = 0;
      while (tries < 120) {
        await new Promise((r) => setTimeout(r, 3000));
        tries++;
        const { data } = await api.get("/weekly-ads");
        setAds(data);
        const cur = data.find((a) => a.id === ad.id);
        if (cur && !cur.generating) {
          if (cur.generate_error) toast.error(cur.generate_error);
          else toast.success(`Generated ${cur.slide_media_ids?.length || 0} TV slide${(cur.slide_media_ids?.length || 0) !== 1 ? "s" : ""} + PDF — pushed to a "Weekly Ad" playlist`);
          break;
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generate failed");
    }
    setBusy(null);
  };

  const sharePdf = async (ad) => {
    const url = `${API}/weekly-ads/${ad.id}/pdf`;
    try {
      const resp = await fetch(url, { cache: "no-store" });
      const blob = await resp.blob();
      const file = new File([blob], `${(ad.title || "weekly-ad").replace(/\s+/g, "-")}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: ad.title });
        return;
      }
      const o = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = o; a.download = file.name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(o);
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't share the PDF");
    }
  };

  const remove = async (ad) => {
    if (!window.confirm(`Delete "${ad.title}"?`)) return;
    await api.delete(`/weekly-ads/${ad.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between gap-2 mb-5">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight">Weekly Ads</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Enter items once → get TV slides + a WhatsApp/social PDF flyer</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditing(null)} data-testid="new-weekly-ad-button"><Plus className="w-4 h-4 mr-1" /> New Weekly Ad</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl w-[calc(100%-1.5rem)] max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-lg md:text-xl pr-6 flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-primary" /> {editing ? "Edit Weekly Ad" : "New Weekly Ad"}</DialogTitle></DialogHeader>
            <Builder initial={editing} business={currentBusiness}
              onSaved={() => { setOpen(false); setEditing(null); load(); }}
              onClose={() => { setOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      {ads.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg py-20 text-center">
          <Newspaper className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">No weekly ads yet.</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Create one, add your items, then generate slides + a PDF.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ads.map((ad) => (
            <div key={ad.id} className="rounded-xl border border-border bg-card p-4 flex flex-col" data-testid={`weekly-ad-${ad.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold truncate">{ad.title}</h3>
                  <p className="text-xs text-muted-foreground">
                    {(THEMES.find((t) => t.key === ad.theme)?.label) || "Weekly Sale"}
                    {(ad.valid_from || ad.valid_to) ? ` · ${ad.valid_from} – ${ad.valid_to}` : ""}
                  </p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full text-white flex-shrink-0" style={{ backgroundColor: ad.accent_color || "#B3122B" }}>
                  {ad.items?.length || 0} items
                </span>
              </div>
              {(ad.end_at || ad.start_at) && (
                <div className="mt-2 text-xs">
                  {ad.active === false || ad.expired_at ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid={`wa-status-${ad.id}`}>
                      <Clock className="w-3 h-3" /> Ended — removed from TVs
                    </span>
                  ) : (ad.start_at && !ad.published && new Date(ad.start_at) > new Date()) ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400" data-testid={`wa-status-${ad.id}`}>
                      <Clock className="w-3 h-3" /> Scheduled — starts {new Date(ad.start_at).toLocaleDateString()}
                    </span>
                  ) : ad.end_at ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-600 dark:text-green-400" data-testid={`wa-status-${ad.id}`}>
                      <Clock className="w-3 h-3" /> Live until {new Date(ad.end_at).toLocaleDateString()}
                    </span>
                  ) : null}
                </div>
              )}
              {ad.generated_at && (
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
                  <span className="flex items-center gap-1"><Tv className="w-3.5 h-3.5" /> {ad.slide_media_ids?.length || 0} slides</span>
                  {ad.pdf_storage_path && <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> PDF ready</span>}
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" onClick={() => generate(ad)} disabled={busy === ad.id || ad.generating} data-testid={`wa-generate-${ad.id}`}>
                  {(busy === ad.id || ad.generating) ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
                  {(busy === ad.id || ad.generating) ? "Generating…" : (ad.generated_at ? "Re-generate" : "Generate")}
                </Button>
                {ad.pdf_storage_path && (
                  <>
                    <Button size="sm" variant="secondary" asChild data-testid={`wa-download-${ad.id}`}>
                      <a href={`${API}/weekly-ads/${ad.id}/pdf`} target="_blank" rel="noreferrer"><Download className="w-4 h-4 mr-1" /> PDF</a>
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => sharePdf(ad)} data-testid={`wa-share-${ad.id}`}>
                      <Share2 className="w-4 h-4 mr-1" /> Share
                    </Button>
                  </>
                )}
                <Button size="sm" variant="ghost" onClick={() => { setEditing(ad); setOpen(true); }} data-testid={`wa-edit-${ad.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(ad)} data-testid={`wa-delete-${ad.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
